import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layouts from "../../../components/Layouts";
import { getDirectusClient } from "../../../lib/directus";
import { readItems, createItem, updateItem, deleteItem } from "@directus/sdk"; // Importar deleteItem
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Spin,
  Popconfirm,
} from "antd"; // Importar message, Spin, Popconfirm
import RespuestasModal from "../components/RespuestasModal";
import validarpermiso from '../../../lib/utils';

const Encuesta = () => {
  
  const navigate = useNavigate();

  // Get the Directus client and user's bodega ID from Redux store
  const directus = getDirectusClient();
  const bodegaId = useSelector((state) => state.userdata.bodega);

  // State to store the surveys fetched from Directus API
  const [encuestas, setEncuestas] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentEncuesta, setCurrentEncuesta] = useState(null);
  const [currentEncuestaid, setCurrentEncuestaid] = useState(null);
  const [bodegas, setBodegas] = useState([]);
  const [form] = Form.useForm(); // Usar form hook de Ant Design
  const [loading, setLoading] = useState(false); // Estado para mostrar el spinner
  const [modalRespuestasVisible, setModalRespuestasVisible] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const userRoleID = useSelector((state) => state.userdata.role?.name);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);

  useEffect(() => {
    const fetchEncuestas = async () => {
      if (!validarpermiso("encuestas", "read", permisos)) {
        setHasPermissionError(true);
        return;
      }

      setLoading(true);
      try {
    
       
        const res = await directus.request(
          readItems("encuestas", {
            fields: ["nombre", "id", "descripcion", "bodega.id", "bodega.nombre", "preguntas.*"],
            filter: {
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                  bodega: {
                    id: { _eq: userBodega },
                  },
                  }
                : {}),
            },
          })
        );
        setEncuestas(res);
        setHasPermissionError(false);
      } catch (error) {
        console.error("Error al recuperar las encuestas:", error);
        if (error.response?.status === 403 || error.message?.includes('permission')) {
          setHasPermissionError(true);
          message.error('No tiene permisos para ver las encuestas');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchBodegas = async () => {
      try {
        const res = await directus.request(
          readItems("bodegas", {
            fields: ["id", "nombre"], 
            filter: {
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    id: { _eq: userBodega },
                  }
                : {}),
            },
          })
        );
        setBodegas(res);
      } catch (error) {
        console.error("Error al recuperar las bodegas:", error);
      }
    };

    // Fetch surveys and bodegas
    if (bodegaId) {
      fetchEncuestas();
      fetchBodegas();
    }
  }, []);

  // Columns to display in the table
  const columns = [
    {
      title: "Nombre", // Column title
      dataIndex: "nombre", // Data field to display in the column
      key: "nombre", // Unique key for the column
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      key: "descripcion",
    },
    {
      title: "Bodega",
      dataIndex: "bodega",
      key: "bodega",
      render: (bodega) => (bodega?.nombre ? bodega.nombre : "No especificado"),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        
        <Space size="middle">
          {validarpermiso("encuestas", "update", permisos) && (
            <Button onClick={() => showEditModal(record)}>
              Editar
            </Button>
          )}
          {validarpermiso("encuestas", "delete", permisos) && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta encuesta?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button>Eliminar</Button>
            </Popconfirm>
          )}
          {validarpermiso("encuestas", "read", permisos) && (
            <Button onClick={() => navigate(`/encuesta/${record.id}`)}>
              Ver
            </Button>
          )}
          {validarpermiso("encuestas", "read", permisos) && (
            <Button
              onClick={() => {
                setModalRespuestasVisible(true);
                setCurrentEncuestaid(record.id);
              }}
              type="primary"
            >
              Respuestas
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setCurrentEncuesta(record);
    setIsEditModalVisible(true);
    form.setFieldsValue({
      nombre: record.nombre,
      descripcion: record.descripcion,
      bodega: record.bodega?.id,
    });
  };

  const handleOk = () => {
    setIsModalVisible(false);
    setIsEditModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsEditModalVisible(false);
    form.resetFields(); // Resetear los campos del formulario al cancelar
  };

  const handleDelete = async (id) => {
    if (!validarpermiso("encuestas", "delete", permisos)) {
      message.error("No tiene permisos para eliminar encuestas");
      return;
    }

    try {
      await directus.request(deleteItem("encuestas", id));
      message.success("Encuesta eliminada con éxito");
      await fetchEncuestas(); // Consultar de nuevo las encuestas
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("No tiene permisos para eliminar encuestas");
      } else {
        message.error("Error al eliminar la encuesta");
      }
      console.error("Error al eliminar la encuesta:", error);
    }
  };

  const fetchEncuestas = async () => {
    if (!validarpermiso("encuestas", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const res = await directus.request(
        readItems("encuestas", {
          fields: ["nombre", "id", "descripcion", "bodega.id", "bodega.nombre", "preguntas.*"],
          filter: {
            ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
              ? {
                bodega: {
                  id: { _eq: userBodega },
                },
                }
              : {}),
          },
        })
      );
      setEncuestas(res);
      setHasPermissionError(false);
    } catch (error) {
      console.error("Error al recuperar las encuestas:", error);
      if (error.response?.status === 403 || error.message?.includes('permission')) {
        setHasPermissionError(true);
        message.error('No tiene permisos para ver las encuestas');
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!validarpermiso("encuestas", "create", permisos)) {
      message.error("No tiene permisos para crear encuestas");
      return;
    }

   
    try {
      const nuevaEncuesta = {
        nombre: values.nombre,
        descripcion: values.descripcion,
        bodega: values.bodega || currentEncuesta?.bodega?.id, // Asegurarse de que la bodega no esté vacía
      };
      await directus.request(createItem("encuestas", nuevaEncuesta));
      message.success("Encuesta guardada con éxito");
      await fetchEncuestas(); // Consultar de nuevo las encuestas
      setIsModalVisible(false); // Cerrar el modal después de guardar
      form.resetFields(); // Resetear los campos del formulario después de guardar
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("No tiene permisos para crear encuestas");
      } else {
        message.error("Error al guardar la encuesta");
      }
      console.error("Error al guardar la encuesta:", error);
    }
  };

  const onEditFinish = async (values) => {
    
    try {
      const updatedEncuesta = {
        nombre: values.nombre,
        descripcion: values.descripcion,
        bodega: values.bodega || currentEncuesta?.bodega?.id, // Asegurarse de que la bodega no esté vacía
      };
      await directus.request(
        updateItem("encuestas", currentEncuesta.id, updatedEncuesta)
      );
      message.success("Encuesta actualizada con éxito");
      await fetchEncuestas(); // Consultar de nuevo las encuestas
      setIsEditModalVisible(false); // Cerrar el modal después de actualizar
      form.resetFields(); // Resetear los campos del formulario después de actualizar
    } catch (error) {
      message.error("Error al actualizar la encuesta");
      console.error("Error al actualizar la encuesta:", error);
    }
  };

  return (
    <Layouts>
      {!hasPermissionError ? (
        <>
        
          {validarpermiso("encuestas", "create", permisos) && (
            <Button 
              type="primary" 
              style={{ marginBottom: 16 }} 
              onClick={showModal}
              disabled={hasPermissionError}
            >
              Crear Nueva Encuesta
            </Button>
          )}
          
          {loading ? (
            <Spin />
          ) : (
            <Table 
              dataSource={encuestas} 
              columns={columns.map(col => {
                if (col.key === 'acciones') {
                  return {
                    ...col,
                    render: (_, record) => (
                      <Space size="middle">
                        {validarpermiso("encuestas", "update", permisos) && (
                          <Button onClick={() => showEditModal(record)}>
                            Editar
                          </Button>
                        )}
                        {validarpermiso("encuestas", "delete", permisos) && (
                          <Popconfirm
                            title="¿Estás seguro de eliminar esta encuesta?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Sí"
                            cancelText="No"
                          >
                            <Button>Eliminar</Button>
                          </Popconfirm>
                        )}
                        {validarpermiso("encuestas", "read", permisos) && (
                          <Button onClick={() => navigate(`/encuesta/${record.id}`)}>
                            Ver
                          </Button>
                        )}
                        {validarpermiso("encuestas", "read", permisos) && (
                          <Button
                            onClick={() => {
                              setModalRespuestasVisible(true);
                              setCurrentEncuestaid(record.id);
                            }}
                            type="primary"
                          >
                            Respuestas
                          </Button>
                        )}
                      </Space>
                    ),
                  };
                }
                return col;
              })} 
              rowKey="id" 
            />
          )}

          <Modal
            title="Crear Nueva Encuesta"
            open={isModalVisible && validarpermiso("encuestas", "create", permisos)}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={null}
          >
            <Form layout="vertical" onFinish={onFinish} form={form}>
              <Form.Item label="Nombre de la Encuesta" name="nombre">
                <Input placeholder="Introduce el nombre de la encuesta" />
              </Form.Item>
              <Form.Item label="Descripción" name="descripcion">
                <Input.TextArea placeholder="Introduce una descripción" />
              </Form.Item>
              <Form.Item label="Selecciona una Bodega" name="bodega">
                <Select placeholder="Selecciona una bodega">
                  {bodegas.map((bodega) => (
                    <Select.Option key={bodega.id} value={bodega.id}>
                      {bodega.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Guardar Encuesta
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Editar Encuesta"
            open={isEditModalVisible && validarpermiso("encuestas", "update", permisos)}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={null}
          >
            <Form
              layout="vertical"
              onFinish={onEditFinish}
              form={form}
              initialValues={currentEncuesta}
            >
              <Form.Item label="Nombre de la Encuesta" name="nombre">
                <Input />
              </Form.Item>
              <Form.Item label="Descripción" name="descripcion">
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="Selecciona una Bodega" name="bodega">
                <Select>
                  {bodegas.map((bodega) => (
                    <Select.Option key={bodega.id} value={bodega.id}>
                      {bodega.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Actualizar Encuesta
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          <RespuestasModal
            visible={modalRespuestasVisible && validarpermiso("encuestas", "read", permisos)}
            onCancel={() => setModalRespuestasVisible(false)}
            encuestaId={currentEncuestaid}
          />
        </>
      ) : (
        <h3>No tiene permisos para ver encuestas</h3>
      )}
    </Layouts>
  );
};

export default Encuesta;
